import React, { useEffect, useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { listTransactions } from '../graphql/queries';
import { deleteTransaction, createAuditLog } from '../graphql/mutations';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import './TransactionList.css';

const client = generateClient({
  authMode: 'userPool'
});

interface Transaction {
  id: string;
  amount: number;
  description: string;
  date: string;
  type: 'EXPENSE' | 'INCOME';
  category: string;
  createdByID: string;
  createdBy: {
    id: string;
    email: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    __typename: string;
  };
  createdAt: string;
  updatedAt: string;
  __typename: string;
}

interface ListTransactionsQuery {
  listTransactions: {
    items: Transaction[];
    nextToken?: string;
  };
}

interface DeleteTransactionMutation {
  deleteTransaction: {
    id: string;
    type: 'EXPENSE' | 'DEPOSIT';
    amount: number;
    category: string;
    description?: string;
    date: string;
    userInitials: string;
    createdAt: string;
    updatedAt: string;
    __typename: string;
  };
}

interface CreateAuditLogMutation {
  createAuditLog: {
    id: string;
    action: string;
    transactionId: string;
    userInitials: string;
    details: string;
    createdAt: string;
    updatedAt: string;
    __typename: string;
  };
}

interface TransactionListProps {
  userId: string;
  refreshKey?: number;
}

const formatDate = (dateStr: string) => {
  // Parse the date string and handle different formats
  const date = new Date(dateStr);
  
  // For dates before today, show the full date and time
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (date < today) {
    // For older dates, show date and time
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } else {
    // For today's dates, emphasize the time
    const timeStr = date.toLocaleTimeString('es-ES', {
      hour: '2-digit', 
      minute: '2-digit'
    });
    const dateStr = date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return `${dateStr} (Hoy, ${timeStr})`;
  }
};

const TransactionList: React.FC<TransactionListProps> = ({ userId, refreshKey = 0 }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ show: boolean; id: string | null }>({
    show: false,
    id: null
  });
  const [filterType, setFilterType] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Add cache-busting version but in a way that doesn't trigger a GraphQL error
      const result = await client.graphql({
        query: listTransactions,
        variables: {
          limit: 100,
          filter: {
            createdByID: {
              ne: null
            }
          }
        }
      }) as GraphQLResult<ListTransactionsQuery>;
      
      if (!result.data) {
        throw new Error('No data received from API');
      }
      
      // Get the partial data even if there are errors
      let items: Transaction[] = [];
      if (result.data.listTransactions && Array.isArray(result.data.listTransactions.items)) {
        items = result.data.listTransactions.items;
      }
      
      // Filter out invalid entries (ones with null createdByID)
      const validItems = items.filter(item => item && item.createdByID);
      
      // Create user object for each transaction with proper names
      const transactionsWithDetails = validItems.map(transaction => {
        // Default user name
        let userName = 'Usuario';
        
        // If transaction already has createdBy data with a name, use it
        if (transaction.createdBy && transaction.createdBy.name) {
          userName = transaction.createdBy.name;
        }
        // Otherwise check by createdByID if needed
        else if (transaction.createdByID === '84088418-c091-7066-1a51-5bb81b6b4010') {
          userName = 'User One';
        } else if (transaction.createdByID === '64d8d4f8-e061-705c-5e39-b9d69649090f') {
          userName = 'User Two';
        } else if (transaction.createdBy && typeof transaction.createdBy === 'string') {
          // If createdBy is directly available as a string, use it
          userName = transaction.createdBy;
        }
        
        // If the transaction already has a complete createdBy object
        if (transaction.createdBy && typeof transaction.createdBy !== 'string' && 
            transaction.createdBy.id && transaction.createdBy.name && transaction.createdBy.email) {
          return transaction;
        }
        
        // Otherwise create a proper createdBy object
        return {
          ...transaction,
          createdBy: {
            id: transaction.createdByID || 'unknown',
            name: userName,
            email: '',
            createdAt: transaction.createdAt,
            updatedAt: transaction.updatedAt,
            __typename: 'User'
          }
        };
      });
      
      setTransactions(transactionsWithDetails);
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      if (error.errors) {
        console.error('GraphQL Errors:', JSON.stringify(error.errors, null, 2));
      }
      if (error.message) {
        console.error('Error message:', error.message);
      }
      if (error.data) {
        console.error('Error data:', error.data);
      }
      const errorMessage = error.message || 'Error al cargar las transacciones';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Main effect to fetch transactions - only on manual refresh or month change
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions, refreshKey, currentDate]);

  const handleDelete = async (id: string) => {
    try {
      // First, get the transaction details for the audit log
      const transactionToDelete = transactions.find(t => t.id === id);
      if (!transactionToDelete) {
        throw new Error('Transaction not found');
      }

      // Delete the transaction
      const deleteResponse = await client.graphql({
        query: deleteTransaction,
        variables: {
          input: { id }
        }
      }) as GraphQLResult<DeleteTransactionMutation>;

      if (deleteResponse.errors) {
        throw new Error(deleteResponse.errors.map(e => e.message).join(', '));
      }

      // Create audit log entry
      const auditLogResponse = await client.graphql({
        query: createAuditLog,
        variables: {
          input: {
            action: 'DELETE',
            entityType: 'Transaction',
            entityId: id,
            changes: `Eliminó la transacción de ${transactionToDelete.type === 'EXPENSE' ? 'gasto' : 'ingreso'} por $${transactionToDelete.amount.toFixed(2)}: "${transactionToDelete.description}" (Categoría: ${transactionToDelete.category || 'No especificada'})`,
            userID: transactionToDelete.createdByID || 'unknown-user'
          }
        }
      }) as GraphQLResult<CreateAuditLogMutation>;

      if (auditLogResponse.errors) {
        console.error('Error creating audit log:', auditLogResponse.errors);
      }

      // Refresh the list after successful deletion
      fetchTransactions();
      setDeleteConfirmation({ show: false, id: null });
    } catch (error: any) {
      console.error('Error deleting transaction:', error);
      alert(`Error al eliminar la transacción: ${error.message || 'Por favor, intente nuevamente.'}`);
    }
  };

  const filteredTransactions = transactions
    .filter(transaction => {
      // First filter by month/year - ensure we have a valid date
      let dateString = transaction.date;
      if (!dateString.includes('T')) {
        dateString = `${dateString}T12:00:00.000Z`;
      }
      
      const transactionDate = new Date(dateString);
      const matchesDate = transactionDate.getMonth() === currentDate.getMonth() &&
        transactionDate.getFullYear() === currentDate.getFullYear();

      // Then filter by type if not "ALL"
      const matchesType = filterType === 'ALL' || transaction.type === filterType;

      // Then filter by search term
      const matchesSearch = searchTerm === '' || 
        transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (transaction.category && transaction.category.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesDate && matchesType && matchesSearch;
    })
    .sort((a, b) => {
      // Force ISO date strings by parsing and reformatting
      const dateAObj = new Date(a.date);
      const dateBObj = new Date(b.date);
      
      // Get timestamps for comparison
      const dateATime = dateAObj.getTime();
      const dateBTime = dateBObj.getTime();
      
      // If we got invalid dates (NaN), fall back to string comparison to avoid breaking the sort
      if (isNaN(dateATime) || isNaN(dateBTime)) {
        return a.date.localeCompare(b.date) * -1; // Reverse for descending
      }
      
      // First sort by date (most recent first)
      if (dateBTime !== dateATime) {
        return dateBTime - dateATime;
      }
      
      // If dates are identical, sort by creation time
      const createdAtA = new Date(a.createdAt).getTime();
      const createdAtB = new Date(b.createdAt).getTime();
      return createdAtB - createdAtA;
    });

  // Calculate totals after sorting

  const totals = filteredTransactions.reduce(
    (acc, transaction) => {
      if (transaction.type === 'EXPENSE') {
        acc.expenses += transaction.amount;
      } else {
        acc.income += transaction.amount;
      }
      return acc;
    },
    { expenses: 0, income: 0 }
  );

  const balance = totals.income - totals.expenses;

  const handlePreviousMonth = () => {
    // Create a new date object to avoid mutating the current one
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    // Create a new date object to avoid mutating the current one  
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const formatMonth = (date: Date) => {
    const month = date.toLocaleString('es-ES', { month: 'long' });
    const year = date.getFullYear();
    return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  };

  if (loading) {
    return <div>Cargando transacciones...</div>;
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={fetchTransactions}>Intentar nuevamente</button>
      </div>
    );
  }

  return (
    <div className="transaction-list">
      <h3>Transacciones</h3>
      
      <div className="month-selector">
        <button onClick={handlePreviousMonth}>&larr;</button>
        <span>{formatMonth(currentDate)}</span>
        <button onClick={handleNextMonth}>&rarr;</button>
      </div>

      <div className="totals-cards">
        <div 
          className={`total-card income ${filterType === 'INCOME' ? 'selected' : ''}`}
          onClick={() => setFilterType(filterType === 'INCOME' ? 'ALL' : 'INCOME')}
          style={{ cursor: 'pointer' }}
        >
          <div className="total-card-content">
            <span className="total-label">Total Ingresos</span>
            <span className="total-amount">${totals.income.toFixed(2)}</span>
          </div>
        </div>
        <div 
          className={`total-card expenses ${filterType === 'EXPENSE' ? 'selected' : ''}`}
          onClick={() => setFilterType(filterType === 'EXPENSE' ? 'ALL' : 'EXPENSE')}
          style={{ cursor: 'pointer' }}
        >
          <div className="total-card-content">
            <span className="total-label">Total Gastos</span>
            <span className="total-amount">${totals.expenses.toFixed(2)}</span>
          </div>
        </div>
        <div 
          className={`total-card balance ${filterType === 'ALL' ? 'selected' : ''}`}
          onClick={() => setFilterType('ALL')}
          style={{ cursor: 'pointer' }}
        >
          <div className="total-card-content">
            <span className="total-label">Balance</span>
            <span className={`total-amount ${balance >= 0 ? 'positive' : 'negative'}`}>
              ${balance.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <div className="transaction-filters">
        <input
          type="text"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredTransactions.length === 0 ? (
        <p>No hay transacciones para este mes.</p>
      ) : (
        <div className="transactions-container">
          {/* Desktop Table View */}
          <div className="desktop-view">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th className="amount-header">Monto</th>
                  <th>Categoría</th>
                  <th>Usuario</th>
                  <th className="action-header">Eliminar</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{formatDate(transaction.date)}</td>
                    <td>
                      <span className={`transaction-type ${transaction.type.toLowerCase()}`}>
                        {transaction.type === 'EXPENSE' ? 'Gasto' : 'Ingreso'}
                      </span>
                    </td>
                    <td className="description-cell">{transaction.description || '-'}</td>
                    <td className={`amount-cell ${transaction.type === 'EXPENSE' ? 'expense' : 'income'}`}>
                      <div className="transaction-amount">
                        {transaction.type === 'EXPENSE' ? '-' : '+'}${transaction.amount.toFixed(2)}
                      </div>
                      <div className="transaction-category">
                        {transaction.category || '-'}
                      </div>
                    </td>
                    <td>{transaction.category || '-'}</td>
                    <td>[{transaction.createdBy.name}]</td>
                    <td className="action-cell">
                      <button 
                        className="delete-button"
                        onClick={() => setDeleteConfirmation({ show: true, id: transaction.id })}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-view">
            {filteredTransactions.map((transaction) => (
              <div key={transaction.id} className="transaction-card">
                <div className="transaction-card-header">
                  <span className="transaction-date">
                    {formatDate(transaction.date)}
                  </span>
                  <span className={`transaction-type ${transaction.type === 'EXPENSE' ? 'expense' : 'income'}`}>
                    {transaction.type === 'EXPENSE' ? 'Gasto' : 'Ingreso'}
                  </span>
                </div>
                <div className="transaction-details">
                  <span className="transaction-description">
                    {transaction.description || '-'}
                  </span>
                  <div className={`transaction-amount ${transaction.type === 'EXPENSE' ? 'expense' : 'income'}`}>
                    {transaction.type === 'EXPENSE' ? '-' : '+'}${transaction.amount.toFixed(2)}
                  </div>
                  <div className="transaction-category">
                    {transaction.category || '-'}
                  </div>
                </div>
                <div className="transaction-footer">
                  <span className="transaction-user">[{transaction.createdBy.name}]</span>
                  <button 
                    className="delete-button"
                    onClick={() => setDeleteConfirmation({ show: true, id: transaction.id })}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation.show && (
        <div className="confirmation-dialog" onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDeleteConfirmation({ show: false, id: null });
        }}>
          <div className="confirmation-content" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
            <div className="confirmation-message">
              ¿Está seguro que desea eliminar esta transacción?
            </div>
            <div className="confirmation-buttons">
              <button
                className="confirmation-button cancel-button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeleteConfirmation({ show: false, id: null });
                }}
              >
                Cancelar
              </button>
              <button 
                className="confirmation-button confirm-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteConfirmation.id && handleDelete(deleteConfirmation.id);
                }}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionList; 